/**
 * ReassignPreviewModal — read-only reassign preview. Demo: /ui-elements/dialogs + /manage-team (change a member's
 * team) + /form-layouts. Per the locked architecture decision the client cannot mutate assignments directly:
 * changes flow through a conversation decision card so the engine writes them and keeps the audit trail. The dialog
 * previews the impact and routes the person to the conversation when they are ready to commit.
 */
import React, { useEffect } from 'react';
import { z } from 'zod';
import { AlertTriangle, Info } from 'lucide-react';
import { zTaskRejectActionRequest } from '../../../generated/api/zod.gen.js';
import { Badge, Button, Modal } from '../../../components/ui';
import { Form, TextFormField, useZodForm } from '../../../components/form/index.js';
import { Avatar, StatusBadge, type EmployeeView } from '../shared';
import type { ReassignDraft } from '../project-context.js';

interface ReassignPreviewModalProps {
  open: boolean;
  onClose: () => void;
  employee: EmployeeView;
  projectName: string;
  onContinue?: (draft: ReassignDraft) => void;
}

/**
 * The draft is not an API payload (it becomes a conversation prompt), so there is no generated schema for it. It
 * starts from the generated free-text `reason` field and adds the three assignment fields.
 */
const draftSchema = zTaskRejectActionRequest
  .pick({ reason: true })
  .extend({ team: z.string(), manager: z.string(), workstream: z.string() });

type DraftValues = z.input<typeof draftSchema>;

const startingValues = (employee: EmployeeView): DraftValues => ({
  team: employee.team ?? '',
  manager: employee.manager ?? '',
  workstream: employee.workstream ?? '—',
  reason: '',
});

export const ReassignPreviewModal: React.FC<ReassignPreviewModalProps> = ({
  open,
  onClose,
  employee,
  projectName,
  onContinue,
}) => {
  const form = useZodForm(draftSchema, { defaultValues: startingValues(employee) });
  const { reset } = form;

  // Re-seed whenever the dialog opens so a cancelled edit does not leak into the next one.
  useEffect(() => {
    if (open) reset(startingValues(employee));
  }, [open, reset, employee]);

  const team = form.watch('team') ?? '';
  const manager = form.watch('manager') ?? '';
  const workstream = form.watch('workstream') ?? '';

  const changed = {
    team: team !== (employee.team ?? ''),
    manager: manager !== (employee.manager ?? ''),
    workstream: workstream !== (employee.workstream ?? '—'),
  };
  const anyChange = changed.team || changed.manager || changed.workstream;

  const tasksAffected = employee.tasksAssigned;
  const decisionsInFlight = employee.decisionsPending;
  const workflowsActive = employee.status === 'working' ? 1 : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reassign ${employee.name}`}
      description="Preview the impact before committing. Confirmed changes go through the project conversation as a decision card, and the engine records an audit entry."
      maxWidth="xl"
    >
      <Form
        form={form}
        onSubmit={(values) =>
          onContinue?.({
            team: values.team,
            manager: values.manager,
            workstream: values.workstream,
            reason: values.reason ?? '',
          })
        }
        className="gap-5"
      >
        <div className="flex items-center gap-3">
          <Avatar label={employee.name} kind={employee.kind} initials={employee.initials} size={42} showKind />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-heading">{employee.name}</div>
            <div className="truncate text-xs text-muted">
              {employee.role} <span className="text-faint">·</span> {employee.tier}
            </div>
          </div>
          <StatusBadge status={employee.status} label={employee.statusLabel} compact />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <p className="mb-1.5 text-sm font-medium text-heading">Project</p>
            <p className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-sm text-muted">
              {projectName}
            </p>
            <p className="mt-1 text-xs text-muted">Moving to another project needs admin approval.</p>
          </div>
          <TextFormField
            name="team"
            label="Team"
            placeholder="Team name"
            description={changed.team ? 'Changed' : undefined}
          />
          <TextFormField
            name="manager"
            label="Manager"
            placeholder="Manager"
            description={changed.manager ? 'Changed' : undefined}
          />
          <TextFormField
            name="workstream"
            label="Workstream"
            placeholder="Workstream"
            description={changed.workstream ? 'Changed' : undefined}
          />
        </div>

        <TextFormField
          name="reason"
          label="Reason (kept in the audit log)"
          placeholder="e.g. Rebalancing load; Sara now owns Pricing v2 end to end"
        />

        <div className="overflow-hidden rounded-xl border border-border bg-surface-alt/40">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div className="text-sm font-semibold text-heading">Impact preview</div>
            <Badge variant={anyChange ? 'warning' : 'neutral'}>{anyChange ? 'Pending changes' : 'No changes'}</Badge>
          </div>
          <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-3">
            <ImpactCell label="Tasks moving" value={anyChange ? tasksAffected : 0} sub="continue under new manager" />
            <ImpactCell
              label="Decisions in flight"
              value={anyChange ? decisionsInFlight : 0}
              sub={decisionsInFlight > 0 ? 'will re-route to new manager' : 'none affected'}
              tone={decisionsInFlight > 0 ? 'warning' : 'neutral'}
            />
            <ImpactCell
              label="Active workflows"
              value={anyChange ? workflowsActive : 0}
              sub={workflowsActive > 0 ? 'checkpoint + resume' : 'queue clean'}
            />
          </div>
          {employee.status === 'blocked' && employee.blockerShort && (
            <div className="flex items-start gap-2 border-t border-border bg-danger-muted/40 px-3 py-2.5 text-xs text-body">
              <AlertTriangle size={12} className="mt-0.5 shrink-0 text-danger" />
              <div>
                <span className="font-semibold text-danger">The existing blocker travels with the assignment.</span> The{' '}
                {employee.blockerShort} issue stays on this employee, so the new manager inherits it.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-md bg-surface-alt p-2.5 text-xs text-muted">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>Continue opens a draft decision card with these changes for review and approval.</span>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!anyChange || !onContinue}
            title={!anyChange ? 'Change at least one field first' : undefined}
          >
            Continue in conversation
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

const ImpactCell: React.FC<{ label: string; value: number; sub: string; tone?: 'warning' | 'neutral' }> = ({
  label,
  value,
  sub,
  tone = 'neutral',
}) => (
  <div className="bg-surface p-3">
    <div className="text-xs font-medium text-muted">{label}</div>
    <div className={`mt-1 font-mono text-2xl font-semibold ${tone === 'warning' ? 'text-warning' : 'text-heading'}`}>
      {value}
    </div>
    <div className="text-xs text-muted">{sub}</div>
  </div>
);
